/**
 * lesson-blueprint.ts
 *
 * SYNCHRONIZED INSTRUCTIONAL RESOURCE CONTRACT
 * ─────────────────────────────────────────────
 * The LessonBlueprint is the single source of truth for all companion generators.
 *
 *   Lesson Plan    → AI-generated; extractBlueprint() runs once after generation.
 *   Presentation   → Blueprint items verbatim → formatted into slides.
 *   Activity Sheet → Blueprint items verbatim → student guided notes.
 *   Assessment     → Same skills, NEW numbers/scenarios (explicitly allowed).
 *
 * Companion generators transform delivery format only. They NEVER re-generate
 * instructional content — numbers, wording, names, and scenarios are preserved
 * exactly as they appear in the lesson the teacher reviewed and approved.
 *
 * ─────────────────────────────────────────────
 *
 * Defines the Lesson Blueprint — the single source of truth extracted from a
 * GeneratedLesson. Companion generators (Activity Sheet, Assessment,
 * Presentation, Homework, Exit Ticket, etc.) transform blueprint items into
 * their target format instead of independently generating instructional content.
 *
 * ─── AUDIENCE CONTRACT ────────────────────────────────────────────────────────
 *
 * Every section carries three distinct content layers, kept completely separate:
 *
 *   section.teacherNotes          — orchestration text: setup instructions,
 *                                   pacing notes, classroom management cues,
 *                                   facilitation moves, debrief prompts.
 *                                   Never shown to students.
 *
 *   item.studentContent           — the task, question, or problem exactly as it
 *                                   should appear on a student worksheet: no
 *                                   narration, no facilitation language, no
 *                                   pacing notes, no management directions.
 *
 *   item.teacherCue               — per-item facilitation language: a debrief
 *                                   question, "look for" note, or discussion
 *                                   prompt attached to a specific item.
 *                                   null when the item has no teacher layer.
 *
 *   item.answer                   — expected answer or worked solution.
 *                                   Never shown on the student worksheet.
 *
 * Companion generator audience rules:
 *   Activity Sheet   → item.studentContent only
 *   Teacher Copy     → teacherNotes + studentContent + teacherCue + answer
 *   Presentation     → teacherNotes (discussion Qs) + studentContent
 *   Answer Key       → studentContent + answer
 */

import type { GeneratedLesson } from './lesson-engine';

// ── Public types ──────────────────────────────────────────────────────────────

/**
 * A single instructional item with its content split by audience.
 *
 * studentContent  → appears on the student worksheet ONLY
 * teacherCue      → facilitation language for teacher copy / presentation only
 * answer          → answer key — never shown on the student worksheet
 */
export interface InstructionalItem {
  /** 1-based position within its section */
  n: number;
  /** Student-facing task, problem, or question — ready for the student worksheet */
  studentContent: string;
  /**
   * Teacher-facing facilitation cue for this specific item: a debrief question,
   * "look for" note, discussion prompt, or classroom management hint.
   * null when the item has no teacher-specific layer.
   */
  teacherCue: string | null;
  /** Expected answer or worked solution — never shown on the student worksheet */
  answer: string;
}

/** A single vocabulary entry extracted from the lesson's vocabulary block. */
export interface VocabTerm {
  term: string;
  definition: string;
  /** Example sentence or usage (empty string when not present) */
  example: string;
}

/**
 * An audience-aware section of the lesson blueprint.
 *
 * teacherNotes → ALL teacher-facing orchestration text for the section:
 *               setup instructions, timing, facilitation moves, management
 *               directions, debrief prompts, discussion questions. This content
 *               is NEVER placed on a student worksheet.
 *
 * items        → student-facing instructional items for this section.
 *               Companion generators for student audiences use ONLY these.
 */
export interface BlueprintSection {
  /**
   * Teacher-facing orchestration text: section setup, pacing notes, management
   * directions, facilitation moves, and post-item debrief prompts.
   * null when no teacher-specific framing exists for this section.
   */
  teacherNotes: string | null;
  /** Audience-separated instructional items */
  items: InstructionalItem[];
}

/**
 * Structured blueprint extracted from a GeneratedLesson.
 *
 * Companion generators transform these items — they NEVER re-generate
 * instructional content. When the lesson changes, the blueprint changes, and
 * all companions that re-render automatically reflect the update.
 */
export interface LessonBlueprint {
  warmUp: BlueprintSection;
  vocabulary: VocabTerm[];
  directInstruction: BlueprintSection;
  guidedPractice: BlueprintSection;
  independentPractice: BlueprintSection;
  exitTicket: BlueprintSection;
  challenge: BlueprintSection;
  homework: BlueprintSection;
  belowLevel: BlueprintSection;
  onLevel: BlueprintSection;
  aboveLevel: BlueprintSection;
}

// ── Blueprint Completeness Validator ─────────────────────────────────────────

export interface BlueprintCompletenessReport {
  /** True when all core sections meet minimum item counts. */
  complete: boolean;
  /** Keys of sections that are missing required items. */
  missingFields: string[];
  /** Human-readable explanation for each missing field. */
  reasons: Record<string, string>;
}

/**
 * Validates that a LessonBlueprint contains enough instructional items to
 * render any companion resource (Presentation, Activity Sheet, Assessment).
 *
 * Call this BEFORE attempting to generate any companion resource.
 * If complete === false, the lesson should be regenerated rather than
 * producing synchronized emptiness.
 */
export function validateBlueprintCompleteness(blueprint: LessonBlueprint): BlueprintCompletenessReport {
  const missingFields: string[] = [];
  const reasons: Record<string, string> = {};

  // Minimum item counts for each core blueprint section.
  // These are the floors that make a lesson teachable.
  const MINIMUMS: [keyof LessonBlueprint, number, string][] = [
    ['warmUp',              1, 'at least 1 warm-up problem'],
    ['directInstruction',   2, 'at least 2 worked examples (teacher modeling)'],
    ['guidedPractice',      3, 'at least 3 guided practice problems (we-do)'],
    ['independentPractice', 4, 'at least 4 independent practice problems (you-do)'],
    ['exitTicket',          1, 'at least 1 exit ticket problem'],
  ];

  for (const [key, min, description] of MINIMUMS) {
    const section = blueprint[key] as BlueprintSection | null | undefined;
    const count   = section?.items?.length ?? 0;
    if (count < min) {
      missingFields.push(key as string);
      reasons[key as string] =
        `${String(key)} has ${count} extracted item(s) — needs ${description}`;
    }
  }

  return { complete: missingFields.length === 0, missingFields, reasons };
}

// ── Audience-classification primitives ───────────────────────────────────────

/**
 * Returns true when the text is a teacher-facing line: facilitation
 * instructions, management notes, debrief prompts, or pacing directions.
 * These lines must NEVER appear on a student worksheet.
 */
export function isTeacherLine(text: string): boolean {
  const t = text.trim();
  return [
    /^(ask|tell|have students|prompt students|instruct|remind|circulate|monitor|facilitate|check for|distribute|display|show students|draw on|write on|model|review|explain to|introduce|provide|call on|debrief|collect|adjust|ensure|observe|project|post)\b/i,
    /^(teacher|teacher's note|teacher note)(\s|:|$)/i,
    /^\(teacher/i,
    /^(transition|look for|listen for|note:|expected:|sample answer:|assessment:)(\s|:)/i,
    /^materials?:/i,
    /^whole[\-\s]class\s+(debrief|share|discussion)/i,
    /^class\s+debrief/i,
    /^partner\s+extension:/i,
    /\(teacher circulates?\)/i,
    /^(success crit|students who finish)/i,
    // Mid-line teacher-verb phrases that slipped through above ────────────────
    /\bteacher\s+(circulates?|works\s+through|monitors|facilitates|reteaches|checks\s+for|models|pulls\s+a?\s*small)\b/i,
    /^round\s+\d+\s*[—\-(]/i,
    /^students\s+(work|complete|solve|respond|attempt|share|compare|record)\s+(independently|silently|on\s+their\s+own|quietly)\b/i,
  ].some(re => re.test(t));
}

/** Strip timing annotations and common boilerplate from text. */
function clean(text: string): string {
  return text
    .replace(/\(≈\s*\d+\s*min\)/g, '')
    .replace(/\(\d+\s*min\)/g, '')
    .replace(/\(think[‑\-]pair[‑\-]share[^)]*\)/gi, '')
    .replace(/\(cold[‑\-]call[^)]*\)/gi, '')
    .replace(/\(have students[^)]*\)/gi, '')
    .replace(/\(partners[^)]*\)/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// ── Text-parsing primitives ───────────────────────────────────────────────────

/**
 * Split a lesson section body into individual raw items.
 * Handles numbered lists (1. / 1)) and bulleted lists (• / - / *).
 * Continuation lines are appended to the current item.
 */
function splitItems(text: string): string[] {
  if (!text?.trim()) return [];
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const items: string[] = [];
  let current = '';
  // Pre-item lines (section titles, instructions, timing) are teacher-orchestration
  // text. They are NOT student-facing items. extractTeacherNotes() captures them
  // separately. Only start collecting items once the first numbered/bullet item is seen.
  let seenItem = false;

  for (const line of lines) {
    if (/^\d+[.)]\s/.test(line)) {
      if (current.trim()) items.push(current.trim());
      current = line.replace(/^\d+[.)]\s*/, '');
      seenItem = true;
    } else if (/^[•\-\*]\s/.test(line)) {
      if (current.trim()) items.push(current.trim());
      current = line.replace(/^[•\-\*]\s*/, '');
      seenItem = true;
    } else if (seenItem) {
      // Continuation of the current item (multi-line problem, note, etc.)
      current = current ? `${current} ${line}` : line;
    }
    // else: pre-item line — silently skip; it goes to extractTeacherNotes() instead
  }
  if (current.trim()) items.push(current.trim());
  return items;
}

/**
 * Parse one raw item string into its three audience layers:
 *   studentContent — the student-facing task stripped of all teacher language
 *   teacherCue     — any per-item facilitation language extracted from the item
 *   answer         — embedded answer, or "Open response"
 *
 * Returns null for teacher-only lines.
 */
/**
 * Detect placeholder text patterns — AI-planning language that describes
 * what a problem should be rather than actually being a problem.
 * Any item matching these patterns is discarded so it never reaches a student worksheet.
 */
export function isPlaceholderItem(text: string): boolean {
  const t = text.trim();
  return [
    /^direct\s+application\s*:/i,
    /^second\s+application\s+with\s+new\s+numbers/i,
    /^apply\s+with\s+one\s+added\s+constraint/i,
    /^multi[\-\s]step\s+problem\s+combining/i,
    /^real[\-\s]world\s+word\s+problem\s+[—–\-]/i,
    /^apply\s+today'?s\s+skill/i,
    /^apply\s+the\s+same\s+approach\s+with\s+different\s+numbers/i,
    /^create\s+and\s+solve\s*:/i,
    /^justify\s+and\s+generalize\s*:/i,
    /^transfer\s*:\s+apply\s+today'?s\s+skill/i,
    /^try\s+today'?s\s+skill\s+with\s+the\s+simplest\s+possible/i,
    /^try\s+one\s+more\s+problem\s+with\s+slightly\s+larger\s+numbers/i,
    /open\s+response\s+using\s+today'?s\s+steps/i,
    /open\s+response\s+[—–\-]\s+check\s+work\s+shown/i,
    // Content that just repeats the skill statement as the problem
    /divide\s+a\s+4[\-\s]digit\s+number\s+by\s+a\s+1[\-\s]digit\s+divisor\s+using\s+the\s+partial\s+quotients\s+strategy.*show\s+complete\s+work/i,
  ].some(re => re.test(t));
}

function parseItem(
  raw: string
): { studentContent: string; teacherCue: string | null; answer: string } | null {
  let text = clean(raw);

  // Pattern 1: "Student practice: …" → extract the sub-item
  const sp = text.match(/student\s+practice\s*[:\-]\s*(.+)/i);
  if (sp) text = clean(sp[1]);

  // Skip entirely teacher-directed text
  if (isTeacherLine(text)) return null;

  // Skip AI-planning placeholder text — these are descriptions of problems,
  // not actual problems. They must never appear on a student worksheet.
  if (isPlaceholderItem(text)) return null;

  let answer = 'Open response';
  let teacherCue: string | null = null;

  // Extract embedded "(Answer: X)" / "(Expected: X)" / "(Solution: X)"
  // The inner pattern (?:[^()]*|\([^)]*\))* handles one level of nested parens so
  // answers like "(Answer: ×3. ³⁄₄ = ⁹⁄₁₂)" are captured in full.
  const em = text.match(/\((answer|expected\s*answer?|solution)\s*:\s*((?:[^()]*|\([^)]*\))*)\)/i);
  if (em) {
    answer = em[2].trim();
    text = text.replace(em[0], '').trim();
  }

  // Extract answer from parenthetical containing variable-equals: "(x = 17; check: …)" or "(LCD = 6: … = ½)"
  if (answer === 'Open response') {
    const varEq = text.match(/\(([a-zA-Z]+\s*=\s*[^)]+)\)/);
    if (varEq) {
      // Take the last "= value" segment as the answer
      const lastEq = varEq[1].match(/=\s*([^;=]+)$/);
      if (lastEq) {
        answer = lastEq[1].trim();
        text = text.replace(varEq[0], '').trim();
      }
    }
  }

  // Extract answer from trailing "expression = number" at end of line (e.g. "… = −13")
  if (answer === 'Open response') {
    const trailEq = text.match(/=\s*(-?[\d¼½¾⅓⅔⅛⅜⅝⅞,.]+)\s*$/);
    if (trailEq) {
      answer = trailEq[1].trim();
      // Replace the "= value" with "= ___" for the student-facing prompt
      text = text.slice(0, text.lastIndexOf(trailEq[0])) + '= ___';
    }
  }

  // Extract inline answer after " — " only when the split is unambiguous:
  // the left side must be a real expression/question (≥12 chars) and the right
  // side must be a brief answer (≤50 chars). This prevents splitting labels
  // like "Part A — instructions" or "Term — Definition" incorrectly.
  if (answer === 'Open response') {
    const di = text.indexOf(' — ');
    if (di >= 12) {
      const right = text.slice(di + 3).trim();
      if (right.length <= 50 && !isTeacherLine(right)) {
        answer = right;
        text = text.slice(0, di).trim();
      }
    }
  }

  // Extract trailing per-item teacher cue:
  //   "Solve: 2/3 + 1/4. Ask: 'Did anyone use a different method?'"
  //   "Compare ¾ and ⅔. (Look for: students using benchmark ½)"
  const teacherCueSuffixRe =
    /[.\s]+\b(Ask:.*|Discuss:.*|Debrief.*|Look for:.*|Listen for:.*|\(Look for[^)]*\)|\(Listen for[^)]*\)|\(Teacher[^)]*\))$/i;
  const cueSuffixMatch = text.match(teacherCueSuffixRe);
  if (cueSuffixMatch) {
    teacherCue = cueSuffixMatch[1].replace(/^\(|\)$/g, '').trim();
    text = text.slice(0, text.length - cueSuffixMatch[0].length).trim();
  }

  // Strip wrapping quotation marks
  text = text
    .replace(/^["'""''\u2018\u2019\u201C\u201D]|["'""''\u2018\u2019\u201C\u201D]$/g, '')
    .trim();

  if (!text || text.length < 6) return null;
  return { studentContent: text, teacherCue, answer };
}

/**
 * Collect all teacher-facing text from a section body:
 *   • Pre-item framing lines (section setup, timing, activity structure)
 *   • Post-item debrief prompts and discussion questions
 *
 * Returns null when no meaningful teacher content is found.
 * This text is NEVER placed on student worksheets.
 */
function extractTeacherNotes(text: string): string | null {
  if (!text?.trim()) return null;
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);

  const teacherLines: string[] = [];
  let seenItem = false;

  for (const line of lines) {
    const isItem = /^\d+[.)]\s/.test(line) || /^[•\-\*]\s/.test(line);

    if (isItem) {
      seenItem = true;
      continue;
    }

    if (!seenItem) {
      // Pre-item lines: everything before the first numbered item is teacher setup.
      // This includes activity names, timing, activity structure descriptions.
      teacherLines.push(line);
    } else {
      // Post-item lines: include teacher-facing directions and discussion questions.
      if (isTeacherLine(line)) {
        teacherLines.push(line);
      } else if (/\?\s*$/.test(line) && line.length < 220) {
        // Discussion questions (end with ?) are teacher facilitation prompts.
        teacherLines.push(line);
      }
    }
  }

  const result = teacherLines
    .join(' ')
    .replace(/\(≈\s*\d+\s*min\)/g, '')
    .replace(/\(\d+\s*min\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  return result.length > 10 ? result : null;
}

// ── Section-specific extractors ───────────────────────────────────────────────

/**
 * Extract a BlueprintSection from any lesson section text.
 * Teacher orchestration text is captured in teacherNotes.
 * Student-facing problems go into items[].studentContent.
 */
function extractSection(sectionText: string, limit = 10): BlueprintSection {
  if (!sectionText?.trim()) return { teacherNotes: null, items: [] };

  const rawItems = splitItems(sectionText);
  const items: InstructionalItem[] = [];

  for (const raw of rawItems) {
    if (items.length >= limit) break;
    const parsed = parseItem(raw);
    if (parsed) items.push({ n: items.length + 1, ...parsed });
  }

  return {
    teacherNotes: extractTeacherNotes(sectionText),
    items,
  };
}

/**
 * Extract a guided practice BlueprintSection with a two-pass strategy:
 *   Pass 1 — prefer "Student practice:" sub-items (student's version of the model)
 *   Pass 2 — fall back to the whole numbered step when no sub-item found
 *
 * Teacher orchestration (activity framing, debrief prompts) goes to teacherNotes.
 */
function extractGuidedSection(text: string): BlueprintSection {
  if (!text?.trim()) return { teacherNotes: null, items: [] };

  const steps = splitItems(text);
  const items: InstructionalItem[] = [];

  for (const step of steps) {
    if (items.length >= 10) break;

    // Prefer "Student practice:" sub-item
    const spMatch = step.match(/student\s+practice\s*[:\-]\s*(.+)/i);
    if (spMatch) {
      const sub = clean(spMatch[1]);
      const parsed = parseItem(sub);
      if (parsed) { items.push({ n: items.length + 1, ...parsed }); continue; }
    }

    // Fall back to the whole step if it contains student-facing content
    const parsed = parseItem(step);
    if (parsed) items.push({ n: items.length + 1, ...parsed });
  }

  return {
    teacherNotes: extractTeacherNotes(text),
    items,
  };
}

/**
 * Regex matching header lines that introduce a teacher-modeled worked example.
 * Handles: "Worked Example 1:", "Model Response 2:", "Example 3:", "Teacher Model:"
 * Requires a number for bare "Example N:" to avoid false-positives on "Example:" used
 * elsewhere (vocabulary definitions, scenario framing, etc.).
 */
const DI_HEADER_RE =
  /^(?:(?:Worked\s+Example|Model\s+Response|Teacher\s+Model)\s*\d*|Example\s+\d+)\s*[:\-—]/i;

/**
 * Teacher-content boundary within a DI block.
 * Lines matching this are never student-facing — they go to item.teacherCue or item.answer.
 */
const DI_TEACHER_BOUNDARY_RE =
  /^(?:Teacher\s+(?:Note|Notes?|Copy|Directions?)\s*[:\-—]|EXEMPLAR\s+RESPONSE\s*:)/i;

/**
 * Extract teacher-modeled worked examples from Direct Instruction text.
 *
 * Supports three formats:
 *   1. Header-delimited blocks — "Worked Example 1:", "Model Response N:", "Example N:",
 *      "Teacher Model:" — each followed by a problem/question, worked steps, optional
 *      Teacher Note, and optional EXEMPLAR RESPONSE.
 *   2. Numbered/bulleted items (legacy or AI variation) containing "Problem:" or
 *      the word "model".
 *   3. Numbered steps mentioning "Worked Example" inline or using early-grade
 *      demonstration language (K–2 lessons: "Let's count", "Watch me", "Show me", etc.).
 */
function extractDirectSection(text: string): BlueprintSection {
  if (!text?.trim()) return { teacherNotes: null, items: [] };

  // Detect header-delimited format by scanning all lines.
  // The AI writes math DI as "Worked Example N:" and writing DI as "Model Response N:"
  // rather than numbered list prefixes, so splitItems() misses them entirely.
  if (text.split('\n').some(l => DI_HEADER_RE.test(l.trim()))) {
    return extractHeaderDelimitedDI(text);
  }

  // Fallback: numbered/bulleted items. Recognise five patterns in order of specificity.
  const steps = splitItems(text);
  const items: InstructionalItem[] = [];
  for (const step of steps) {
    if (items.length >= 6) break;
    const cleaned = clean(step);

    // Pattern 1 — "Problem: ..." keyword (math DI legacy format)
    const problemMatch = cleaned.match(/^problem\s*:\s*(.{4,120})/i);
    if (problemMatch) {
      const answerMatch = cleaned.match(/\(Answer:\s*(.*?)\)/i);
      items.push({
        n: items.length + 1,
        studentContent: `Example: ${problemMatch[1].trim()}`,
        teacherCue: 'Model this example — students record in notes',
        answer: answerMatch ? answerMatch[1].trim() : 'Teacher models — record in notes',
      });
      continue;
    }

    // Pattern 2 — "model(ing) <topic>" verb phrase
    const modelMatch = cleaned.match(/\bmodel(?:ing)?\s+(.{10,90}?)(?:\.|,|$)/i);
    if (modelMatch) {
      items.push({
        n: items.length + 1,
        studentContent: `Example: ${modelMatch[1].trim()}`,
        teacherCue: 'Model this example — students record in notes',
        answer: 'Teacher models — record in notes',
      });
      continue;
    }

    // Pattern 3 — inline "Worked Example N" or "Teacher Model" mention in a numbered step.
    // This catches the generic DI fallback format produced by lesson-engine.ts before the
    // template was fixed to use proper headers, as well as any existing lessons in the DB.
    if (/worked\s+example\s*\d*|teacher\s+model\s*\d*/i.test(cleaned) && cleaned.length >= 20) {
      items.push({
        n: items.length + 1,
        studentContent: cleaned.slice(0, 300),
        teacherCue: 'Teacher models — students observe and take notes',
        answer: 'Teacher models — record in notes',
      });
      continue;
    }

    // Pattern 4 — Early-grade (K–2) teacher demonstration language.
    // Kindergarten DI uses guided counting, shared practice, and manipulative demonstration
    // rather than formal "Worked Example" headers. Any step that opens with these verbs
    // describes teacher modeling and should be treated as a DI item.
    const EARLY_GRADE_DEMO_RE =
      /^(?:let'?s\s+(?:count|try|look|practice|sort|match|build|make|show|trace|say|clap|point)\b|watch\s+me\b|show\s+me\b|together\s+(?:let'?s|we'll|we\s+will)\b|count\s+(?:with\s+me|all\s+together|along|on)\b|touch\s+each\b|hold\s+up\b|point\s+to\b|we'?ll\s+(?:count|build|sort|place|match|trace)\b|place\s+(?:a|the|your)\b|i\s+have\s+\d+\b|start\s+with\s+\d+\b)/i;
    if (EARLY_GRADE_DEMO_RE.test(cleaned) && cleaned.length >= 15) {
      items.push({
        n: items.length + 1,
        studentContent: cleaned.slice(0, 300),
        teacherCue: 'Teacher models — students participate and observe',
        answer: 'Teacher demonstrates — students respond',
      });
      continue;
    }
  }
  return { teacherNotes: extractTeacherNotes(text), items };
}

/**
 * Parse Direct Instruction text formatted as header-delimited blocks.
 *
 * Each block structure (math):
 *   Worked Example 1:
 *   Problem: 23 × 4
 *   Step 1: Decompose 23 → 20 + 3   ← student notes
 *   …
 *   (Answer: 92)
 *
 * Each block structure (writing — CER/RACE):
 *   Model Response 1:
 *   Question: Why do plants need sunlight?
 *   CLAIM: Plants need sunlight because …   ← student notes
 *   EVIDENCE: …
 *   Teacher Note: Point out that …          ← goes to item.teacherCue
 *   EXEMPLAR RESPONSE: CLAIM: …            ← goes to item.answer
 *
 * Audience rules:
 *   item.studentContent — problem/question + worked scaffold (no teacher content)
 *   item.teacherCue     — Teacher Note text for this example
 *   item.answer         — EXEMPLAR RESPONSE, or inline "(Answer: …)", or default
 */
function extractHeaderDelimitedDI(text: string): BlueprintSection {
  const PROBLEM_RE = /^(?:Problem|Question)\s*:\s*/i;
  const ANSWER_INLINE_RE = /\(Answer:\s*(.*?)\)/i;

  // Split the full text into blocks, one per DI header line.
  const lines = text.split('\n');
  const blocks: string[][] = [];
  let current: string[] = [];
  for (const line of lines) {
    if (DI_HEADER_RE.test(line.trim())) {
      if (current.length > 0) blocks.push(current);
      current = [line];
    } else {
      current.push(line);
    }
  }
  if (current.length > 0) blocks.push(current);

  const items: InstructionalItem[] = [];

  for (const block of blocks) {
    if (items.length >= 6) break;
    // Skip any preamble text that appeared before the first DI header.
    if (!DI_HEADER_RE.test(block[0]?.trim() ?? '')) continue;

    const studentLines: string[] = [];
    let teacherNoteText = '';
    let exemplarText = '';
    let standaloneAnswerText = ''; // captures "Answer: X" lines (AI math DI format)
    let inTeacherNote = false;
    let inExemplar = false;

    for (let i = 1; i < block.length; i++) {
      const trimmed = block[i].trim();
      if (!trimmed) {
        // Preserve blank lines in the student section (e.g. scaffold spacing).
        if (!inTeacherNote && !inExemplar) studentLines.push('');
        continue;
      }
      if (DI_TEACHER_BOUNDARY_RE.test(trimmed)) {
        if (/^EXEMPLAR\s+RESPONSE/i.test(trimmed)) {
          inExemplar = true;
          inTeacherNote = false;
          exemplarText = trimmed.replace(/^EXEMPLAR\s+RESPONSE\s*:\s*/i, '').trim();
        } else {
          inTeacherNote = true;
          inExemplar = false;
          teacherNoteText = trimmed
            .replace(/^Teacher\s+(?:Note|Notes?|Copy|Directions?)\s*[:\-—]\s*/i, '')
            .trim();
        }
      } else if (inExemplar) {
        exemplarText += ' ' + trimmed;
      } else if (inTeacherNote) {
        teacherNoteText += ' ' + trimmed;
      } else {
        // Detect standalone "Answer: X" lines — the AI math DI prompt instructs the
        // AI to write these as their own line rather than inline "(Answer: X)".
        // Capture them here so they don't end up in student-facing content.
        const standaloneAns = trimmed.match(/^Answer\s*:\s*(.+)$/i);
        if (standaloneAns) {
          standaloneAnswerText = standaloneAns[1].trim();
        } else {
          studentLines.push(block[i]);
        }
      }
    }

    // studentContent: prefer an explicit "Problem:" or "Question:" line; otherwise
    // use all non-teacher student lines joined (covers CLAIM/EVIDENCE/REASONING scaffold).
    const problemLine = studentLines.find(l => PROBLEM_RE.test(l.trim()));
    let studentContent: string;
    if (problemLine) {
      studentContent = `Example: ${problemLine.trim().replace(PROBLEM_RE, '')}`;
    } else {
      const body = studentLines.map(l => l.trim()).filter(Boolean).join('\n');
      studentContent = body.length > 5 ? body : '';
    }
    if (!studentContent) continue;

    // answer priority: EXEMPLAR RESPONSE > standalone "Answer:" line > inline "(Answer: ...)" > default
    const inlineAnswer = studentLines.join('\n').match(ANSWER_INLINE_RE);
    const answer = exemplarText.trim()
      ? exemplarText.trim()
      : standaloneAnswerText
        ? standaloneAnswerText
        : inlineAnswer
          ? inlineAnswer[1].trim()
          : 'Teacher models — record in notes';

    items.push({
      n: items.length + 1,
      studentContent,
      teacherCue: teacherNoteText.trim() || 'Model this example — students record in notes',
      answer,
    });
  }

  return {
    teacherNotes: extractTeacherNotes(text),
    items,
  };
}

/** Extract vocabulary terms (term + definition + example) from the vocabulary block. */
function extractVocab(vocabText: string): VocabTerm[] {
  if (!vocabText?.trim()) return [];
  const lines = vocabText.split('\n').map(l => l.trim()).filter(Boolean);
  const terms: VocabTerm[] = [];

  for (const line of lines) {
    const stripped = line.replace(/^\d+\.\s*/, '').replace(/^[•\-]\s*/, '');
    const dashIdx = stripped.indexOf(' — ');
    const colonIdx = stripped.indexOf(': ');
    let term = '';
    let rest = '';

    if (dashIdx > -1) {
      term = stripped.slice(0, dashIdx).trim();
      rest = stripped.slice(dashIdx + 3).trim();
    } else if (colonIdx > -1) {
      term = stripped.slice(0, colonIdx).trim();
      rest = stripped.slice(colonIdx + 2).trim();
    } else {
      term = stripped;
    }

    if (!term) continue;

    const exIdx = rest.toLowerCase().indexOf('example:');
    let definition = rest;
    let example = '';
    if (exIdx > -1) {
      definition = rest.slice(0, exIdx).trim().replace(/\.$/, '');
      example = rest.slice(exIdx + 8).trim();
    }

    terms.push({ term, definition, example });
  }

  return terms.slice(0, 10);
}

// ── Challenge-problem fallback extractor ──────────────────────────────────────

/**
 * The AI sometimes writes a single challenge problem as a plain paragraph
 * without a "1." number prefix, which `splitItems` will silently skip.
 * This function detects that case and wraps the whole block as item 1
 * so the blueprint extractor can produce a parseable result.
 *
 * Only used for the challenge section — other sections require proper numbering.
 */
function extractChallengeSection(text: string): BlueprintSection {
  if (!text?.trim()) return { teacherNotes: null, items: [] };

  // First pass: try the standard numbered extractor (limit 5 problems)
  const standard = extractSection(text, 5);
  if (standard.items.length > 0) return standard;

  // Fallback: the text has substance but no numbered items — the AI wrote
  // it as a prose block. Collapse to a single line and run through parseItem.
  const collapsed = text.trim().replace(/\n+/g, ' ').replace(/\s{2,}/g, ' ');
  if (collapsed.length < 10) return { teacherNotes: null, items: [] };

  const parsed = parseItem(collapsed);
  if (!parsed) return { teacherNotes: null, items: [] };

  return {
    teacherNotes: null,
    items: [{ n: 1, ...parsed }],
  };
}

// ── Prose section extractor for writing/ELA lessons ──────────────────────────

/**
 * Writing-strategy labels that signal a structured response scaffold.
 * CER: CLAIM, EVIDENCE, REASONING
 * RACE: RESTATE, ANSWER, CITE, EXPLAIN
 * Generic: ASSERT, SUPPORT, ANALYZE, PART A/B/C, SCENARIO, QUESTION
 */
const WRITING_LABEL_RE =
  /^(CLAIM|EVIDENCE|REASONING|RESTATE|ANSWER|CITE|EXPLAIN|ASSERT|SUPPORT|ANALYZE|PART\s+[A-Z]|SCENARIO|QUESTION|INTRODUCTION|BODY|CONCLUSION)\s*:/i;

/**
 * Pattern marking the transition from student-facing content to teacher-only content.
 * The `(?:^|\n)` prefix makes this match at the start of text OR after any newline,
 * so it's safe for full-text inline splitting (where the match index is the split point).
 * Do NOT use this to test a paragraph string — it will fire mid-paragraph.
 */
const TEACHER_BOUNDARY_RE =
  /(?:^|\n)(?:Teacher\s+(?:Directions?|Copy|Notes?|Version)\s*[:\-—]|EXEMPLAR\s+RESPONSE\s*:|Teacher\s+Note\s*:)/i;

/**
 * Same semantic content as TEACHER_BOUNDARY_RE but anchored to the START of a string (^).
 * Use this to classify whether a paragraph (trimmed) begins with teacher content,
 * rather than merely contains teacher content somewhere in the middle.
 */
const TEACHER_PARA_START_RE =
  /^(?:Teacher\s+(?:Directions?|Copy|Notes?|Version)\s*[:\-—]|EXEMPLAR\s+RESPONSE\s*:|Teacher\s+Note\s*:)/i;

/**
 * Pattern that detects the start of a new student task when the parser is in
 * teacher mode. Recognises the common AI task-header formats used in writing lessons.
 * Used only while inTeacher === true to decide when to transition back to student mode.
 */
const NEW_STUDENT_TASK_RE =
  /^(?:(?:Extension\s+)?Task\s+\d+|Writing\s+Task\s+\d+|Challenge\s+\d+|Enrichment\s+(?:Task\s+)?\d+|Passage\s*[:\-]|Scenario\s*[:\-]|You-Do\b|I-Do\b|We-Do\b|Source\s+[AB]\s*[:\-])/i;

/**
 * Parse a writing section into one or more (student, teacher) block pairs.
 *
 * Handles the common multi-task pattern:
 *
 *   Student Task 1 content           ← student mode
 *   Teacher Copy — Exemplar 1        ← enters teacher mode
 *   [blank or more exemplar lines]
 *   Student Task 2 content           ← NEW_STUDENT_TASK_RE triggers reset
 *   Teacher Copy — Exemplar 2        ← re-enters teacher mode
 *
 * Uses a line-by-line state machine rather than paragraph splitting so that
 * tasks separated only by single newlines (no blank line) are handled correctly.
 * TEACHER_PARA_START_RE (^-anchored) is used to detect teacher boundary lines,
 * preventing mid-line teacher markers from mis-classifying student content.
 *
 * Returns an array of {student, teacher} pairs in document order.
 * Single-block sections return a one-element array.
 */
export function parseWritingBlocks(text: string): { student: string; teacher: string }[] {
  const lines = text.split('\n');
  const blocks: { student: string; teacher: string }[] = [];
  let studentLines: string[] = [];
  let teacherLines: string[] = [];
  let inTeacher = false;

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();

    if (TEACHER_PARA_START_RE.test(trimmed)) {
      // Start of teacher section
      inTeacher = true;
      teacherLines.push(rawLine);
    } else if (inTeacher) {
      // In teacher section — check if this line starts a new student task
      if (trimmed.length > 5 && NEW_STUDENT_TASK_RE.test(trimmed)) {
        // Emit current pair, begin fresh student section
        blocks.push({ student: studentLines.join('\n'), teacher: teacherLines.join('\n') });
        studentLines = [rawLine];
        teacherLines = [];
        inTeacher = false;
      } else {
        teacherLines.push(rawLine);
      }
    } else {
      studentLines.push(rawLine);
    }
  }

  // Emit final block
  if (studentLines.length > 0 || teacherLines.length > 0) {
    blocks.push({ student: studentLines.join('\n'), teacher: teacherLines.join('\n') });
  }

  return blocks.length > 0 ? blocks : [{ student: text, teacher: '' }];
}

/**
 * Parse CER/RACE labeled components from the teacher (exemplar) half of a section.
 * Returns a label→exemplar map so extractProseSection can populate item.answer
 * with real exemplar content instead of "Open response" or blank underscores.
 */
export function extractExemplarMap(teacherText: string): Record<string, string> {
  const map: Record<string, string> = {};
  if (!teacherText) return map;

  // Strip teacher header line(s) so labels inside are found cleanly.
  // Works for both multi-line ("Teacher Copy:\nCLAIM: ...") and inline
  // ("Teacher Copy — EXEMPLAR RESPONSE: CLAIM: ... EVIDENCE: ...") formats.
  const stripped = teacherText
    .replace(/^(?:Teacher\s+(?:Directions?|Copy|Notes?|Version)\s*[:\-—]|EXEMPLAR\s+RESPONSE\s*:|Teacher\s+Note\s*:)/im, '')
    .trim();

  // Normalise newlines → spaces so inline and multi-line formats are equivalent.
  // Example inline: "RESTATE: text ANSWER: text CITE: text EXPLAIN: text"
  // Example multi-line: "RESTATE: text\nANSWER: text\nCITE: text\nEXPLAIN: text"
  // Both become the same string after normalisation.
  const normalized = stripped.replace(/\n+/g, ' ').replace(/\s{2,}/g, ' ');

  // Unified label-position scan: find each label boundary, slice the value
  // between consecutive label positions.
  const LABEL_RE =
    /\b(CLAIM|EVIDENCE|REASONING|RESTATE|ANSWER|CITE|EXPLAIN|ASSERT|SUPPORT|ANALYZE)\s*:/gi;
  const matches = [...normalized.matchAll(LABEL_RE)];

  for (let i = 0; i < matches.length; i++) {
    const label = matches[i][1].toUpperCase();
    const start = matches[i].index! + matches[i][0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index! : normalized.length;
    const value = normalized.slice(start, end).trim();
    if (value.length > 3) map[label] = value;
  }

  // When the teacher copy is an unlabeled prose exemplar (e.g. a complete CER paragraph
  // without component labels), no entries are added above.  Store the prose under the
  // reserved key '_PROSE' so callers can fall back to it instead of showing blank answers.
  if (Object.keys(map).length === 0 && stripped.length > 10) {
    map['_PROSE'] = normalized.slice(0, 800);
  }

  return map;
}

/** Extract labeled items (CLAIM/EVIDENCE etc.) from a single text block. */
export function extractLabeledItems(
  text: string,
  exemplar: Record<string, string>,
  limit: number,
): InstructionalItem[] {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const labeled: { label: string; content: string }[] = [];
  let current: { label: string; content: string } | null = null;
  const preambleLines: string[] = [];
  let firstLabelSeen = false;

  for (const line of lines) {
    if (WRITING_LABEL_RE.test(line)) {
      firstLabelSeen = true;
      if (current) labeled.push(current);
      const colonIdx = line.indexOf(':');
      current = {
        label: line.slice(0, colonIdx).toUpperCase().trim(),
        content: line.slice(colonIdx + 1).trim(),
      };
    } else if (current) {
      // Stop accumulating student content when teacher-only annotation starts.
      // Defense-in-depth for when parseWritingBlocks couldn't separate a mixed paragraph.
      if (TEACHER_PARA_START_RE.test(line)) {
        labeled.push(current);
        current = null;
        break;
      }
      current.content = current.content ? `${current.content} ${line}` : line;
    } else if (!firstLabelSeen && !TEACHER_PARA_START_RE.test(line)) {
      // Collect scenario/source lines that appear before any writing label.
      // These are student-readable context (question, source passage) — not phase labels.
      preambleLines.push(line);
    }
  }
  if (current) labeled.push(current);

  // Build a scenario-context preamble from unlabeled pre-label lines.
  // Strip structural phase headers ("We-Do Writing Practice", "You-Do Practice", etc.)
  // since those are section labels, not student-readable source content.
  const scenarioLines = preambleLines.filter(
    l => !/^(?:You-?Do|We-?Do|I-?Do)[\s\-]+(Writing\s+)?Practice\b/i.test(l) && l.length > 8,
  );
  const scenarioPreamble = scenarioLines.join('\n').trim();

  // Only inject a leading scenario item when the labeled items are blank scaffolds
  // (i.e., GP / IP / ET, not pre-filled DI teacher models).
  const hasScaffolds = labeled.some(s => s.content.includes('_') || s.content.length <= 3);
  const leadingItems: InstructionalItem[] = [];
  if (scenarioPreamble && hasScaffolds) {
    leadingItems.push({ n: 1, studentContent: scenarioPreamble, teacherCue: null, answer: '' });
  }

  const offset    = leadingItems.length;
  const remaining = Math.max(0, limit - offset);

  const labeledResult = labeled.slice(0, remaining).map((s, i) => {
    const exemplarAns = exemplar[s.label] ?? '';
    const rawContent = s.content.replace(/_+/g, '').trim();
    // Answer priority:
    //   1. Per-label exemplar extracted from teacher copy (CLAIM: answer, EVIDENCE: answer…)
    //   2. Prose fallback: when teacher copy is an unlabeled paragraph, use the whole
    //      exemplar for every scaffold item (repeated, but far better than blank scaffolds)
    //   3. Raw content (scaffold with blanks stripped, only useful if content is pre-filled)
    //   4. 'Open response' when nothing better is available
    const proseFallback = exemplar['_PROSE'] ?? '';
    const isScaffold = s.content.includes('_') || rawContent.length <= 3;
    const answer = exemplarAns
      || (isScaffold ? proseFallback : '')
      || (rawContent.length > 3 ? rawContent : 'Open response');
    return {
      n: offset + i + 1,
      // studentContent shows content for pre-filled sections, bare label for blank scaffolds
      studentContent: rawContent.length > 3 ? `${s.label}: ${s.content}` : `${s.label}:`,
      teacherCue: null,
      answer,
    };
  });

  return [...leadingItems, ...labeledResult];
}

/**
 * Extract blueprint items from a prose writing lesson section.
 *
 * Priority order:
 *   1. Standard numbered items (via extractSection) — AI may still number writing tasks
 *   2. Multi-block parsing via parseWritingBlocks — handles sections with multiple tasks
 *      each followed by their own exemplar (Task 1 → Exemplar 1 → Task 2 → Exemplar 2).
 *      For each (student, teacher) pair: extract labeled items from student half only,
 *      populate answer fields from the paired teacher exemplar.
 *   3. Single prose block fallback — whole student half as one writing prompt
 *
 * Used only for writing/ELA lessons. Math lessons always use extractSection.
 */
function extractProseSection(text: string, limit = 10): BlueprintSection {
  if (!text?.trim()) return { teacherNotes: null, items: [] };

  // 1. Standard numbered extraction — AI may still number writing tasks
  const standard = extractSection(text, limit);
  if (standard.items.length > 0) return standard;

  // 2. Parse into paired (student, teacher) blocks.
  //    Each task's exemplar is paired with that task's student scaffold,
  //    so multiple enrichment tasks each get their own exemplar answers.
  const writingBlocks = parseWritingBlocks(text);

  const allItems: InstructionalItem[] = [];
  const teacherNotesParts: string[] = [];

  for (const { student: rawStudent, teacher: rawTeacher } of writingBlocks) {
    if (allItems.length >= limit) break;

    // Inline-split fallback: when parseWritingBlocks couldn't separate student and
    // teacher halves at a paragraph boundary (AI used only single newlines), fall back
    // to a character-level split at the first teacher boundary marker within the block.
    let studentText = rawStudent;
    let teacherText = rawTeacher;
    if (!rawTeacher && TEACHER_BOUNDARY_RE.test(rawStudent)) {
      const m = rawStudent.match(TEACHER_BOUNDARY_RE);
      if (m?.index !== undefined) {
        const idx = m.index + (m[0].startsWith('\n') ? 1 : 0);
        studentText = rawStudent.slice(0, idx).trim();
        teacherText = rawStudent.slice(idx).trim();
      }
    }

    const exemplar = extractExemplarMap(teacherText);
    if (teacherText) teacherNotesParts.push(teacherText);

    // Try labeled extraction (CLAIM: / EVIDENCE: / REASONING: etc.) from student half
    const labeled = extractLabeledItems(studentText, exemplar, limit - allItems.length);
    if (labeled.length > 0) {
      labeled.forEach(it => allItems.push({ ...it, n: allItems.length + 1 }));
    } else if (studentText.trim().length >= 15) {
      // No labeled structure — treat entire student block as one writing prompt
      const collapsed = studentText.trim().replace(/\n+/g, ' ').replace(/\s{2,}/g, ' ');
      const allExemplars = Object.values(exemplar).join('\n');
      allItems.push({
        n: allItems.length + 1,
        studentContent: collapsed.slice(0, 600),
        teacherCue: null,
        answer: allExemplars || 'Open response',
      });
    }
  }

  if (allItems.length > 0) {
    return { teacherNotes: teacherNotesParts.join('\n\n') || null, items: allItems };
  }

  // 3. Final fallback: whole text as a single writing prompt
  const fallbackStudent = writingBlocks[0]?.student ?? text;
  const collapsed = fallbackStudent.trim().replace(/\n+/g, ' ').replace(/\s{2,}/g, ' ');
  if (collapsed.length < 15) return { teacherNotes: null, items: [] };

  const fallbackExemplar = extractExemplarMap(writingBlocks[0]?.teacher ?? '');
  const answer = Object.values(fallbackExemplar).join('\n') || 'Open response';
  return {
    teacherNotes: teacherNotesParts.join('\n\n') || null,
    items: [{ n: 1, studentContent: collapsed.slice(0, 800), teacherCue: null, answer }],
  };
}

// ── Main entry point ──────────────────────────────────────────────────────────

/**
 * Extract an audience-aware LessonBlueprint from a GeneratedLesson.
 *
 * Called once at the end of generateLesson(). The result is stored on
 * lesson.blueprint and shared by all companion generators.
 *
 * COMPANION GENERATOR AUDIENCE RULES (enforced by type, not just convention):
 *   Activity Sheet   → section.items[i].studentContent
 *   Teacher Copy     → section.teacherNotes + all item fields
 *   Presentation     → section.teacherNotes (discussion Qs) + items[i].studentContent
 *   Answer Key       → items[i].studentContent + items[i].answer
 */
export function extractBlueprint(lesson: GeneratedLesson): LessonBlueprint {
  const empty: BlueprintSection = { teacherNotes: null, items: [] };
  const challengeText = lesson.extensionActivity ?? '';

  // Route writing lessons (ELA / CER / RACE) through the prose extractor.
  // Math lessons use the standard numbered-item extractor.
  const lowerStrat = (lesson.spec?.requiredStrategy ?? '').toLowerCase();
  const writing =
    lesson.subject === 'English Language Arts' ||
    lowerStrat.includes('cer') ||
    lowerStrat.includes('race') ||
    lowerStrat.includes('writing strategy');

  // Extractors selected based on lesson type
  const xPractice = (text: string, limit: number) =>
    writing ? extractProseSection(text, limit) : extractSection(text, limit);
  const xGuided = (text: string) =>
    writing ? extractProseSection(text, 10) : extractGuidedSection(text);
  const xDirect = (text: string) =>
    writing ? extractProseSection(text, 6) : extractDirectSection(text);

  return {
    warmUp:              xPractice(lesson.warmUp, 5),
    vocabulary:          extractVocab(lesson.vocabulary),
    directInstruction:   xDirect(lesson.directInstruction),
    guidedPractice:      xGuided(lesson.guidedPractice),
    independentPractice: xPractice(lesson.independentPractice, 10),
    exitTicket:          xPractice(lesson.exitTicket, 4),
    // Writing lessons: use prose extractor for challenge (preserves multi-task structure).
    // Math lessons: use the challenge-specific extractor with its prose collapse fallback.
    challenge:           writing ? xPractice(challengeText, 5) : extractChallengeSection(challengeText),
    homework:            xPractice(lesson.homework ?? '', 6),
    belowLevel:          lesson.tieredPractice
      ? xPractice(lesson.tieredPractice.belowLevel, 5)
      : empty,
    onLevel:             lesson.tieredPractice
      ? xPractice(lesson.tieredPractice.onLevel, 5)
      : empty,
    aboveLevel:          lesson.tieredPractice
      ? xPractice(lesson.tieredPractice.aboveLevel, 5)
      : empty,
  };
}

/**
 * Compute a content-stable key from lesson instructional sections.
 * Changes whenever the lesson is regenerated. Used by companion generators
 * to detect stale cached outputs and trigger re-synchronization.
 *
 * COVERAGE: All five core teaching sections plus vocabulary.
 * Previously only 4 sections × 120 chars — edits to directInstruction or
 * vocabulary were invisible to the syncKey and would not trigger stale
 * warnings or Update Linked Resources patches.
 */
export function lessonContentKey(lesson: GeneratedLesson): string {
  // Each section contributes a <prefix:length> fingerprint so changes to
  // EITHER the beginning OR the total length of any section are detected.
  // This catches appended items AND in-place substitutions of equal length
  // as long as they touch the first 60 chars or change the total length.
  // Sections are joined with '|||' — a sequence that never appears in lesson
  // content and is safe in PostgreSQL JSON columns (no NUL bytes).
  const sk = (v: string | undefined) => {
    const t = String(v ?? '');
    return `${t.slice(0, 60)}:${t.length}`;
  };
  return [
    sk(lesson.warmUp),
    sk(lesson.directInstruction),
    sk(lesson.guidedPractice),
    sk(lesson.independentPractice),
    sk(lesson.exitTicket),
    sk(lesson.vocabulary),
  ].join('|||');
}
